<template>
  <div class="lvmmed-link-settings">
    <div class="card radius-10 border-0">
      <div class="bound-border p-10-10">
        <div class="header-row">
          <h3 class="section-title">Liên kết tài khoản LVMMED</h3>
          <el-button type="primary" size="small" :loading="loadingStatus" @click="fetchLinkStatus">Làm mới</el-button>
        </div>

        <div v-if="loadingStatus" class="state-text">Đang tải trạng thái liên kết...</div>

        <template v-else>
          <div v-if="isLinked" class="linked-box">
            <div class="linked-title">Đã liên kết thành công</div>
            <div class="linked-item"><strong>Tên tổ chức:</strong> {{ linkStatus.lvmmed_org_info?.name || '-' }}</div>
            <div class="linked-item"><strong>ID tổ chức:</strong> {{ linkStatus.lvmmed_org_info?.id || '-' }}</div>
            <div class="linked-item"><strong>Thời điểm liên kết:</strong> {{ formatDate(linkStatus.linked_at) }}</div>

            <div class="action-row">
              <el-button type="danger" plain :loading="unlinkLoading" @click="handleUnlink">Unlink</el-button>
            </div>
          </div>

          <div v-else class="unlinked-box">
            <div class="state-text">Tài khoản chưa liên kết LVMMED.</div>
            <p class="hint">Liên kết bằng tài khoản LVMMED thông qua OAuth.</p>
            <div class="action-row">
              <el-button type="primary" @click="handleOAuthStart">Liên kết bằng OAuth</el-button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import lvmmedService from '@/services/lvmmedService'

const OAUTH_STATE_STORAGE_KEY = 'hodo_lvmmed_oauth_state'

export default {
  name: 'LvmmedLinkSettings',
  data () {
    return {
      loadingStatus: false,
      linkStatus: null,
      unlinkLoading: false
    }
  },
  computed: {
    isLinked () {
      return Number(this.linkStatus?.link_status) === 1
    }
  },
  mounted () {
    this.fetchLinkStatus()
    window.addEventListener('message', this.handleOAuthMessage)
  },
  beforeDestroy () {
    window.removeEventListener('message', this.handleOAuthMessage)
  },
  methods: {
    formatDate (value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('vi-VN')
    },
    getErrorMessage (error, fallback = 'Đã có lỗi xảy ra. Vui lòng thử lại.') {
      const message = error?.response?.data?.message || error?.response?.data?.status || fallback
      return message
    },
    openMessage (type, message) {
      if (this.$message && this.$message[type]) {
        this.$message[type](message)
        return
      }
      this.$toast && this.$toast.open({ message, type })
    },
    async fetchLinkStatus () {
      this.loadingStatus = true
      try {
        const data = await lvmmedService.getLinkStatus()
        this.linkStatus = data || null
      } catch (error) {
        this.openMessage('error', this.getErrorMessage(error, 'Không lấy được trạng thái liên kết LVMMED.'))
      } finally {
        this.loadingStatus = false
      }
    },
    getOAuthRedirectUri () {
      return `${window.location.origin}/#/lvmmed/oauth/callback`
    },
    async handleOAuthStart () {
      try {
        const initData = await lvmmedService.oauthInit({ redirectUri: this.getOAuthRedirectUri() })
        const authorizeUrl = initData?.authorize_url || ''
        const state = initData?.state || ''

        if (!authorizeUrl || !state) {
          this.openMessage('error', 'Không lấy được URL OAuth từ LVMMED.')
          return
        }

        window.localStorage.setItem(OAUTH_STATE_STORAGE_KEY, state)
        window.open(authorizeUrl, '_blank', 'width=840,height=720')
      } catch (error) {
        this.openMessage('error', this.getErrorMessage(error, 'Không thể khởi tạo OAuth LVMMED.'))
      }
    },
    async handleOAuthMessage (event) {
      if (event.origin !== window.location.origin) return
      const payload = event.data || {}
      if (payload?.type !== 'LVMMED_OAUTH_RESULT') return

      if (payload?.success) {
        await this.fetchLinkStatus()
        this.openMessage(this.isLinked ? 'success' : 'warning', this.isLinked ? 'Đã liên kết thành công.' : (payload?.message || 'Callback OAuth thành công nhưng trạng thái chưa liên kết.'))
        return
      }

      this.openMessage('error', payload?.message || 'Liên kết OAuth thất bại.')
    },
    async handleUnlink () {
      try {
        await this.$confirm('Bạn có chắc chắn muốn hủy liên kết LVMMED?', 'Xác nhận', {
          confirmButtonText: 'Đồng ý',
          cancelButtonText: 'Hủy',
          type: 'warning'
        })
      } catch (e) {
        return
      }

      this.unlinkLoading = true
      try {
        await lvmmedService.unlink()
        this.openMessage('success', 'Đã hủy liên kết LVMMED.')
        await this.fetchLinkStatus()
      } catch (error) {
        this.openMessage('error', this.getErrorMessage(error, 'Không thể hủy liên kết LVMMED.'))
      } finally {
        this.unlinkLoading = false
      }
    }
  }
}
</script>

<style scoped>
.lvmmed-link-settings {
  padding: 8px;
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.section-title {
  margin: 0;
  color: #111827;
  font-size: 20px;
  font-weight: 700;
}

.state-text {
  color: #334155;
}

.linked-box,
.unlinked-box {
  border: 1px solid #dbe4f0;
  border-radius: 12px;
  background: #f8fbff;
  padding: 16px;
}

.linked-title {
  color: #0f766e;
  font-weight: 700;
  margin-bottom: 10px;
}

.linked-item {
  margin-bottom: 6px;
  color: #1f2937;
}

.action-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.hint {
  color: #64748b;
  font-size: 13px;
}

</style>
